import axios from '@/plugins/axios'
import type { Evaluation, Session, Student } from '@/types/global'
import { context } from '.'

export type EvaluationRequest = {
  session_id: number
  student_id: number
  id?: number
  presence_type: 'present'
  surah_memorize_from_id?: number
  verse_memorize_from?: number
  surah_memorize_to_id?: number
  verse_memorize_to?: number
  total_memorize_line?: number
  total_memorize_page?: number

  memorize_school_error_ids?: number[]
}

export const evaluate = async (evaluation: {}, date: Date = new Date()) => {
  const response = await axios.post<{
    status: boolean
    message: string
  }>(`/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/daily_evaluation`, {
    jsonrpc: '2.0',
    params: {
      ...evaluation,
      date: getFormattedDate(date)
    }
  })

  return response
}

export const getEvaluation = async (session: Session, student: Student, date = new Date()) => {
  const response = await axios.post<{ result: Evaluation }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/daily_evaluation/onchange_student`,
    {
      jsonrpc: '2.0',
      params: {
        session_id: session.id,
        student_id: student.id,
        date: getFormattedDate(date) ?? getFormattedDate()
      }
    }
  )

  return response.data.result
}

export const getTodaySingleSessionEvaluations = async (
  session: Session,
  date: Date,
  fields?: string[]
) => {
  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        fields,
        filter: [
          ['session_id', 'in', [session.id]],
          ['date', '=', getFormattedDate(date) ?? getFormattedDate()],
          ['student_id', 'not in', []]
        ]
      }
    }
  )

  return response.data.result
}

export const fetchAllTodayEvaluations = async (
  date: Date = new Date(),
  filters: [string, string, string | Array<any>][] = [
    ['session_id', 'not in', []],
    ['date', '=', getFormattedDate(date)],
    ['student_id', 'not in', []]
  ]
) => {
  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        filter: filters
      }
    }
  )

  return response.data.result
}

export const getTodayEvaluations = async (
  sessions: Session[],
  dates: { [key: number]: Date },
  fields?: string[]
) => {
  const result: { [key: number]: Evaluation[] } = {}

  // const response = await fetchAllTodayEvaluations()
  for (const session of sessions) {
    const evaluations = await getTodaySingleSessionEvaluations(
      session,
      dates?.[session.id] ?? new Date(),
      fields
    )

    result[session.id] = evaluations
  }

  return result
}

export const getRangeEvaluations = async (
  from: string,
  to: string,
  filters?: [string, string, string | Array<any>][] | undefined,
  fields?: string[]
) => {
  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        order: 'date',
        limit: 999999,
        fields,
        filter: [
          // ['session_id', 'not in', []],
          ['date', '<=', to],
          ['date', '>=', from],
          ['student_id', 'not in', []],
          ...(filters ?? [])
        ]
      }
    }
  )

  if (!response.data.result.length) return []

  return response.data.result
}

export const getStudentRangeEvaluations = async (
  from: string,
  to: string,
  studentId: number,
  filters?: [string, string, string | Array<any>][] | undefined,
  fields?: string[]
) => {
  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        order: 'date',
        limit: 999999,
        fields,
        filter: [
          ['session_id', 'not in', []],
          ['date', '<=', to],
          ['date', '>=', from],
          ['student_id', 'in', [studentId]],
          ...(filters ?? [])
        ]
      }
    }
  )

  if (!response.data.result.length) return []

  return response.data.result
}

export const getSessionsRangeEvaluations = async (
  sessions: Session[],
  from: string,
  to: string,
  filters = [],
  fields?: string[]
) => {
  const result: { [key: number]: Evaluation[] } = {}

  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        order: 'create_date',
        limit: 999999,
        fields,
        filter: [
          ['session_id', 'in', sessions.map((s) => s.id)],
          ['date', '<=', to],
          ['date', '>=', from],
          ['student_id', 'not in', []],
          ...filters
        ]
      }
    }
  )

  if (!response.data.result.length) return {}

  for (const session of sessions) {
    result[session.id] = response.data.result.filter((e) => e.session_id.id == session.id)
  }

  return result
}

export const getRangeStudentEvaluations = async (
  session: Session,
  from: string,
  to: string,
  student: Student
) => {
  const result: { [key: number]: Evaluation[] } = {}

  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation`,
    {
      jsonrpc: '2.0',
      params: {
        order: 'create_date',
        filter: [
          ['session_id', 'in', [session.id]],
          ['date', '<=', to],
          ['date', '>=', from],
          ['student_id', 'in', [student.id]]
        ]
      }
    }
  )

  if (!response.data.result.length) return []

  return response.data.result
}

function getFormattedDate(date?: Date | null) {
  const selectedDate =
    date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date()
  return `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`
}

export const markPresence = async (evaluation: {}, date: Date = new Date()) => {
  const response = await evaluate(evaluation, date)

  return response
}

export const cancelEvaluation = async (evaluation_id: number) => {
  const response = await axios.post<{ result: boolean }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/daily_evaluation/${evaluation_id}/delete`,
    {
      jsonrpc: '2.0',
      params: {}
    }
  )

  return response.data.result
}

export const getStudentRangeEvaluationsGuest = async (
  from: string,
  to: string,
  studentId: number,
  filters?: [string, string, string | Array<any>][] | undefined,
  fields?: string[]
) => {
  const response = await axios.post<{ result: Evaluation[] }>(
    `/api-proxy/${context}/api/${import.meta.env.VITE_API_VERSION}/evaluation/view`,
    {
      jsonrpc: '2.0',
      params: {
        check_token: 272333,
        order: 'date',
        limit: 999999,
        fields,
        filter: [
          ['session_id', 'not in', []],
          ['date', '<=', to],
          ['date', '>=', from],
          ['student_id', 'in', [studentId]],
          ...(filters ?? [])
        ]
      }
    }
  )

  if (!response.data.result.length) return []

  return response.data.result
}
